import addInfoBarMessage from './addInfoBarMessage';
import cancelSend from './cancelSend';
import closeCompose from './closeCompose';
import { messageBeingSent } from './messageBeingSent';
import moveComposeToTab from './moveComposeToTab';
import onInlineComposeSendStateChange from './onInlineComposeSendStateChange';
import onSendMessageSucceeded from './onSendMessageSucceeded';
import removeInfoBarMessage from './removeInfoBarMessage';
import setAsyncSendState from './setAsyncSendState';
import setIsSending from './setIsSending';
import setPreferAsyncSend from './setPreferAsyncSend';
import { resetComposeItemId } from './smimeActions';
import trySaveMessage, { isSaving } from './trySaveMessage';
import datapoints, { MailComposeSendDp } from '../datapoints';
import { findComposePlugins } from '../plugins/ComposePlugins';
import { ComposeViewState, AsyncSendState } from 'owa-mail-compose-store';
import { moreDetailsNotificationLabel } from '../strings.locstring.json';
import checkSendAsAddressAndAddToMru from '../utils/checkSendAsAddressAndAddToMru';
import { getAllRecipients } from '../utils/getAllRecipientsAsEmailAddressStrings';
import getFriendlySubject from '../utils/getFriendlySubject';
import getMailTipsViewStateMap from '../utils/getMailTipsViewStateMap';
import getRecipientsFromWellViewState from '../utils/getRecipientsFromWellViewState';
import { handleSendErrorResponse } from '../utils/handleSendErrorResponse';
import { sendAndSaveInfobarIdsToRemove } from '../utils/InfoBarMessageId';
import { logAttachmentDatapoints } from '../utils/logAttachmentDatapoints';
import { ComposeScenario, logSmartReplyDatapoint } from '../utils/logSmartReplyComposeDatapoint';
import { logSubjectPickerDatapoint } from '../utils/logSubjectPickerDatapoint';
import safelyGetSmartReplyExtractionId from '../utils/safelyGetSmartReplyExtractionId';
import { UNDO_SEND_ERROR_MESSAGE } from '../utils/sendConstants';
import { CustomError, handleSuccessResponse, sendMessage } from '../utils/sendSaveUtils';
import shouldUseAsyncSend from '../utils/shouldUseAsyncSend';
import validateSend from '../utils/validateSend';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import endSession from 'owa-controls-findpeople-feedback-manager/lib/actions/endSession';
import { addMilliseconds, now } from 'owa-datetime';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc, { format } from 'owa-localize';
import {
    isGroupScenario,
    isViewStateDirty,
} from 'owa-mail-compose-actions/lib/utils/viewStateUtils';
import { getDelayedItemStore, lazyRemoveMailItemWithData } from 'owa-mail-delaysend-store';
import showNotificationOnSend from 'owa-mail-delaysend-store/lib/utils/showNotificationOnSend';
import { logSendActionMailTipsDatapoint } from 'owa-mail-tips/lib/datapoints';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import setCurrentBccRecipient from 'owa-smime/lib/actions/setCurrentBccRecipient';
import setSmimeBccForkingMode from 'owa-smime/lib/actions/setSmimeBccForkingMode';
import SmimeBccForkingMode from 'owa-smime/lib/store/schema/SmimeBccForkingMode';
import SmimeBccForkingSetting from 'owa-smime/lib/store/schema/SmimeBccForkingSetting';
import { isSxSDisplayed } from 'owa-sxs-store';
import {
    infobarMessageSMIMEBccForkingFailedForRecipients,
    failToSendMessageError,
} from './trySendMessage.locstring.json';

import {
    DatapointStatus,
    PerformanceDatapoint,
    logUsage,
    returnTopExecutingActionDatapoint,
    ActionDatapoint,
} from 'owa-analytics';
import {
    lazyAddNotification,
    ensureNotificationHandlerRegistered,
    NotificationActionType,
    NotificationViewState,
} from 'owa-header-toast-notification';

function asyncSend(
    viewState: ComposeViewState,
    startSendDatapoint: () => void,
    e2eDatapoint: ActionDatapoint
): Promise<ItemInfoResponseMessage> {
    const delaySendIntervalSeconds = getUserConfiguration().UserOptions.MailSendUndoInterval
        ? getUserConfiguration().UserOptions.MailSendUndoInterval
        : 0;

    // Start to send here before other code to avoid any client side error interrupt sending
    const sendMessagePromise: Promise<ItemInfoResponseMessage> =
        delaySendIntervalSeconds > 0 && !isSxSDisplayed()
            ? handleDelayedSendMessage(
                  viewState,
                  startSendDatapoint,
                  delaySendIntervalSeconds * 1000
              )
            : handleSendMessage(viewState, startSendDatapoint);

    setIsSending(viewState, true /*isSending*/);

    e2eDatapoint.markUserPerceivedTime(true);

    return sendMessagePromise;
}

async function smimeSyncSend(viewState: ComposeViewState): Promise<ItemInfoResponseMessage> {
    let responseMessage: ItemInfoResponseMessage;
    const { smimeViewState } = viewState;
    const bccEncryptedEmailForking = getUserConfiguration().SmimeAdminSettings
        .BccEncryptedEmailForking;
    const bccRecipients = getRecipientsFromWellViewState(viewState.bccRecipientWell);
    const bccFailedRecipients = [];

    try {
        if (
            viewState.toRecipientWell.recipients.length > 0 ||
            viewState.ccRecipientWell.recipients.length > 0
        ) {
            // Send the first fork with TO and CC recipients.
            setSmimeBccForkingMode(smimeViewState, SmimeBccForkingMode.IncludeOnlyToAndCc);
            responseMessage = await sendMessage(viewState);
            resetComposeItemId(viewState);
        }

        if (bccEncryptedEmailForking == SmimeBccForkingSetting.SeparateCopyToEachBcc) {
            // BccEncryptedEmailForking = 0 -> Separate copy to each BCC recipients.
            let currentBccRecipient = 0;
            setSmimeBccForkingMode(
                smimeViewState,
                SmimeBccForkingMode.IncludeSingleBccInEachMessage
            );
            while (currentBccRecipient < bccRecipients.length) {
                setCurrentBccRecipient(smimeViewState, currentBccRecipient);
                if (!responseMessage) {
                    // Send the first fork to first BCC recipient, when TO and CC recipients are not present.
                    responseMessage = await sendMessage(viewState);
                } else {
                    // Send subsequent BCC forks to each BCC recipient.
                    await sendMessage(viewState).catch(() => {
                        bccFailedRecipients.push(bccRecipients[currentBccRecipient]);
                    });
                }
                viewState.itemId && resetComposeItemId(viewState);
                currentBccRecipient++;
            }
        } else if (bccEncryptedEmailForking == SmimeBccForkingSetting.SingleCopyToAllBcc) {
            // BccEncryptedEmailForking = 1 -> Single copy to all BCC recipients
            setSmimeBccForkingMode(
                smimeViewState,
                SmimeBccForkingMode.IncludeAllBccInSingleMessage
            );
            if (!responseMessage) {
                // Send the first fork to all BCC recipients, when TO and CC recipients are not present.
                responseMessage = await sendMessage(viewState);
            } else {
                // Send subsequent BCC fork to all BCC recipients.
                await sendMessage(viewState).catch(() => {
                    bccFailedRecipients.push(...bccRecipients);
                });
            }
        }

        if (bccFailedRecipients.length > 0) {
            const infobarMessage = format(
                loc(infobarMessageSMIMEBccForkingFailedForRecipients),
                bccFailedRecipients.map(recipient => recipient.Name).join(', ')
            );
            addInfoBarMessage(viewState, 'smimeBccForkingFailedError', [infobarMessage]);
            logUsage('errorSmimeBccForkingFailed', [bccFailedRecipients.length], { isCore: true });
            throw new Error('smimeBccForkingFailedError') as CustomError;
        }
    } catch (e) {
        return Promise.reject(e);
    }

    logUsage('successSmimeBccForking', [bccRecipients.length, bccEncryptedEmailForking], {
        isCore: true,
    });
    return Promise.resolve(responseMessage);
}

function shouldForkMessage(viewState: ComposeViewState): boolean {
    const bccEncryptedEmailForking = getUserConfiguration().SmimeAdminSettings
        .BccEncryptedEmailForking;
    return (
        isFeatureEnabled('cmp-smime') &&
        viewState.smimeViewState &&
        viewState.smimeViewState.shouldEncryptMessageAsSmime &&
        viewState.bccRecipientWell &&
        viewState.bccRecipientWell.recipients.length > 0 &&
        bccEncryptedEmailForking != SmimeBccForkingSetting.SingleCopyToAllRecipients
    );
}

async function syncSend(
    viewState: ComposeViewState,
    startSendDatapoint: () => void
): Promise<ItemInfoResponseMessage> {
    startSendDatapoint();
    setIsSending(viewState, true /*isSending*/);

    const responseMessage = shouldForkMessage(viewState)
        ? await smimeSyncSend(viewState)
        : await sendMessage(viewState);

    endSession(viewState.findPeopleFeedbackManager, 'Send');
    closeCompose(viewState);

    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, true /*isSendPending*/);
    }

    return Promise.resolve(responseMessage);
}

function handleSendMessage(
    viewState: ComposeViewState,
    startSendDatapoint: () => void
): Promise<ItemInfoResponseMessage> {
    startSendDatapoint();
    logAttachmentDatapoints(viewState);

    setAsyncSendState(viewState, AsyncSendState.Sending);
    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, true /*isSendPending*/);
    }
    // Set to preferAsyncSend to false in case the send failed, then the retry will use sync send
    setPreferAsyncSend(viewState, false /* preferAsyncSend */);

    return sendMessage(viewState)
        .then(responseMessage => {
            // Handle the response here to avoid false alerm of sending failure caused by client side error
            setAsyncSendState(viewState, AsyncSendState.Sent);
            return Promise.resolve(responseMessage);
        })
        .catch((error: CustomError) => {
            // Only show the send failure notification when we really got server side sending error
            handleFailedOrCanceledAsyncSend(viewState, error);
            error.showErrorToUser = true;
            return Promise.reject(error);
        });
}

function handleDelayedSendMessage(
    viewState: ComposeViewState,
    startSendDatapoint: () => void,
    delaySendIntervalMilliseconds: number
): Promise<ItemInfoResponseMessage> {
    setAsyncSendState(viewState, AsyncSendState.Delay);
    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, true /*isSendPending*/);
    }

    return new Promise<ItemInfoResponseMessage>((resolve, reject) => {
        // Start delay timer and show Undo send notification bar
        const delaySendTimer = window.setTimeout(async function() {
            lazyRemoveMailItemWithData.importAndExecute(viewState);
            try {
                resolve(await handleSendMessage(viewState, startSendDatapoint));
            } catch (error) {
                reject(error);
            }
        }, delaySendIntervalMilliseconds);
        showNotificationOnSend(delaySendIntervalMilliseconds);

        // Update the delayedSendStore and prep for cancel send
        getDelayedItemStore().delayedMailItems.push({
            timeoutId: delaySendTimer,
            timeToSend: addMilliseconds(now(), delaySendIntervalMilliseconds),
            data: viewState,
            afterCancel: data => {
                cancelSend(data);
                handleFailedOrCanceledAsyncSend(viewState);
                reject(new Error(UNDO_SEND_ERROR_MESSAGE));
            },
        });

        // Save draft
        if (!isGroupScenario(viewState) && !isSaving(viewState) && isViewStateDirty(viewState)) {
            trySaveMessage(viewState, true /*isAutoSave*/);
        }
    });
}

function onAsyncSendNotificationClick(notification: NotificationViewState) {
    const viewState = notification.actionContext as ComposeViewState;
    moveComposeToTab(viewState, true /* isShown */, true /* makeActive */);
}

function addErrorNotification(viewState: ComposeViewState) {
    const subject = getFriendlySubject(viewState.subject);

    ensureNotificationHandlerRegistered(
        NotificationActionType.AsyncSendFailed,
        onAsyncSendNotificationClick
    );

    const notification = {
        actionType: NotificationActionType.AsyncSendFailed,
        title: loc(failToSendMessageError),
        description: subject,
        actionLink: loc(moreDetailsNotificationLabel),
        cancelLink: null,
        actionContext: viewState,
    };

    lazyAddNotification.importAndExecute(notification as NotificationViewState);
}

function handleFailedOrCanceledAsyncSend(viewState: ComposeViewState, error?: CustomError) {
    if (viewState.isInlineCompose) {
        onInlineComposeSendStateChange(viewState, false /*isSendPending*/);
    }

    // Only do this in Sending state because Sending means the viewState is still pending,
    // while it will be Timeout state after a period, in that case the viewState is floating rather than pending/
    // We don't need to show error notification for floating compose because the form itself is already visible
    if (viewState.asyncSendState == AsyncSendState.Sending) {
        // Don't notify for send failure if the reason for the failure is hip challenge related.
        if (
            !error ||
            error.message !== 'ErrorMessageSubmissionBlocked' ||
            !isFeatureEnabled('cmp-hipCheckChallengeOnSendFail')
        ) {
            addErrorNotification(viewState);
        }
    }

    if (viewState.asyncSendState != AsyncSendState.None) {
        setAsyncSendState(viewState, AsyncSendState.Error);
    }

    setIsSending(viewState, false /*isSending*/);
}

function endCtq(
    datapoint: PerformanceDatapoint,
    smartReplyExtractionId: string,
    viewState: ComposeViewState,
    error?: CustomError
) {
    if (datapoint) {
        window.requestAnimationFrame(() => {
            // Add extraction ID for smart reply, so we can generate meaningful experiment data.
            datapoint.addCosmosOnlyData(
                JSON.stringify({ smartReplyExtractionId: smartReplyExtractionId })
            );

            if (error) {
                datapoint.addCustomData(
                    datapoint.eventName == MailComposeSendDp
                        ? [error.showErrorToUser, error.isAsyncSend]
                        : {
                              showErrorToUser: error.showErrorToUser,
                              isAsyncSend: error.isAsyncSend,
                              composeTraceId: viewState.logTraceId,
                              operation: viewState.operation,
                          }
                );
                datapoint.endWithError(DatapointStatus.ServerError, error);
            } else {
                datapoint.end();
            }
        });
    }
}

export default createLegacyActionWithDatapoint('trySendMessage', datapoints.MailComposeSendE2E)(
    async function trySendMessage(
        viewState: ComposeViewState,
        bypassClientSideValidation?: boolean
    ) {
        if (viewState.isSending) {
            // Avoid duplicated sending
            return Promise.resolve();
        }
        const mailComposeSendE2EDataPoint = returnTopExecutingActionDatapoint();
        const useAsyncSend = shouldUseAsyncSend(viewState);

        removeInfoBarMessage(viewState, sendAndSaveInfobarIdsToRemove);

        const errorCode =
            !bypassClientSideValidation &&
            (await validateSend(viewState, mailComposeSendE2EDataPoint));

        if (!errorCode) {
            messageBeingSent();

            // Track the real sending failure, client side validation failure is excluded
            let sendDatapoint: PerformanceDatapoint = null;

            const startSendDatapoint = () => {
                sendDatapoint = new PerformanceDatapoint(
                    isGroupScenario(viewState) ? 'GroupMailComposeSend' : 'MailComposeSend',
                    {
                        isQos: true,
                    }
                );
            };

            const sendFunction = useAsyncSend ? asyncSend : syncSend;

            let responseMessage: ItemInfoResponseMessage = null;
            try {
                responseMessage = await sendFunction(
                    viewState,
                    startSendDatapoint,
                    mailComposeSendE2EDataPoint
                );

                if (isGroupScenario(viewState)) {
                    sendDatapoint.addCustomData({
                        composeTraceId: viewState.logTraceId,
                        operation: viewState.operation,
                    });
                }

                // Check if the From address should be added to the MRU and, if so, add it (VSO 34513)
                checkSendAsAddressAndAddToMru(viewState.fromViewState);
            } catch (error) {
                error.isAsyncSend = useAsyncSend;
                setIsSending(viewState, false /*isSending*/);
                endCtq(sendDatapoint, safelyGetSmartReplyExtractionId(viewState), viewState, error);
                handleSendErrorResponse(error, viewState, true /*isSend*/);
                return Promise.reject(error);
            }

            // contentOperationPlugin will do auto save when dispose. Since we have already sent the message, we need to stop this action
            const plugins = findComposePlugins(viewState);
            if (plugins && plugins.contentOperationPlugin) {
                plugins.contentOperationPlugin.stopSaveOnDispose();
            }

            // Log datapoints based on properties of message.
            if (viewState.IRMData) {
                logUsage('MailComposeSendWithIRMData');
            }
            if (viewState.isReadReceiptRequested) {
                logUsage('MailComposeSendRequestReadReceipt');
            }
            if (viewState.isDeliveryReceiptRequested) {
                logUsage('MailComposeSendRequestDeliveryReceipt');
            }
            if (viewState.sensitivity) {
                logUsage('MailComposeSendWithMessageSensitivity');
            }

            logSubjectPickerDatapoint(ComposeScenario.Send, viewState);

            // If we are sending a message created via smart reply, log a datapoint.
            logSmartReplyDatapoint(
                ComposeScenario.Send,
                safelyGetSmartReplyExtractionId(viewState)
            );

            logSendActionMailTipsDatapoint(
                getMailTipsViewStateMap(viewState.ccRecipientWell, getAllRecipients(viewState))
            );
            setIsSending(viewState, false /*isSending*/);
            endCtq(sendDatapoint, safelyGetSmartReplyExtractionId(viewState), viewState);
            const handledSuccessfully = handleSuccessResponse(responseMessage, viewState);
            onSendMessageSucceeded(viewState.operation, viewState.referenceItemId);

            if (handledSuccessfully) {
                return Promise.resolve();
            } else {
                mailComposeSendE2EDataPoint.endWithError(
                    DatapointStatus.ServerError,
                    new Error('Malformed response shape')
                );
                // Returning as a regular resolve here, as I'm not sure of the side effects.
                return Promise.resolve();
            }
        } else {
            setIsSending(viewState, false /*isSending*/);
            mailComposeSendE2EDataPoint.endWithError(
                DatapointStatus.UserError,
                new Error(errorCode)
            );
            return Promise.reject(new Error(errorCode));
        }
    }
);
